import React, { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import logoSvg from 'assets/images/sprite/logo.svg'

import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { eventLogger } from 'services/eventLogger.service'
import { setZenDeskWidget } from 'helpers/setZenDeskWidget'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from 'components/LoadingButton'
import { TermsOfUseLink } from 'components/TermsOfUseLink'
import { PrivacyPolicyLink } from 'components/PrivacyPolicyLink'
import { getPageIdFromPathName } from 'helpers/getPageIdFromPathName'
import { PageId } from 'page-constants'
import { useSelector } from 'react-redux'
import { selectCurrentVariantCohort } from 'root-redux/selects/common'
import { MENU_BG_COLORS } from 'components/Header/constants'
import { Cohort } from 'root-constants'
import { SvgImage } from '../SvgImage'
import { StyledHeader as S } from './Header.styles'

export const Header: React.FC = () => {
  const cohort = useSelector(selectCurrentVariantCohort)
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false)
  const [isZendeskLoading, setIsZendeskLoading] = useState<boolean>(false)

  const { t } = useTranslation()
  const { hasBurgerMenu } = useGetPageInfo()
  const { pathname } = useLocation()

  const isResultPage = useMemo(
    () => getPageIdFromPathName(pathname) === PageId.RESULT,
    [pathname],
  )

  const handleZendeskToggle = useCallback(() => {
    window.zE('webWidget', 'show')
    window.zE('webWidget', 'open')
    eventLogger.logNeedHelpClicked()
  }, [])

  const handleHelpClick = useCallback(() => {
    const isZendeskEmbedded = window.zE

    if (!isZendeskEmbedded) {
      setIsZendeskLoading(true)
      setZenDeskWidget(handleZendeskToggle, setIsZendeskLoading)
      return
    }

    handleZendeskToggle()
  }, [handleZendeskToggle])

  const toggleMenuVisibility = useCallback(() => {
    setIsMenuVisible(!isMenuVisible)
  }, [isMenuVisible])

  if (isResultPage) return null

  if (hasBurgerMenu) {
    return (
      <>
        <S.Wrapper>
          <S.LogoContainer>
            <SvgImage svg={logoSvg} width={44} height={44} />
            <S.LogoText>Mood Balance</S.LogoText>
          </S.LogoContainer>
          <S.HeaderActions>
            {isZendeskLoading && (
              <LoadingButton type="button" onClick={handleHelpClick} />
            )}
            {!isZendeskLoading && (
              <S.SupportAction onClick={handleHelpClick}>
                {t('actions.needHelp')}
              </S.SupportAction>
            )}
            <S.Burger type="button" onClick={toggleMenuVisibility}>
              <S.BurgerLine />
              <S.BurgerLine />
              <S.BurgerLine />
            </S.Burger>
          </S.HeaderActions>
        </S.Wrapper>
        {isMenuVisible && <S.Backdrop onClick={toggleMenuVisibility} />}
        <S.Menu
          isMenuVisible={isMenuVisible}
          backgroundColor={MENU_BG_COLORS[cohort || Cohort.VARIANT_ONE]}
        >
          <S.MenuCloseButton type="button" onClick={toggleMenuVisibility} />
          <S.StyledMenuLinkContainer>
            <TermsOfUseLink />
          </S.StyledMenuLinkContainer>
          <S.StyledMenuLinkContainer>
            <PrivacyPolicyLink />
          </S.StyledMenuLinkContainer>
        </S.Menu>
      </>
    )
  }

  return (
    <S.SingleWrapper>
      <S.LogoContainer>
        <SvgImage svg={logoSvg} width={44} height={44} />
        <S.LogoText>Mood Balance</S.LogoText>
      </S.LogoContainer>
    </S.SingleWrapper>
  )
}
